import {isAbstractSketchEdgeObject} from '../../../mesh/guards/isAbstractSketchEdgeObject';
import type {AbstractSketchEdgeObject} from '../../../mesh/sketch/AbstractSketchEdgeObject';
import type {
	SketchEntityReferenceDiagnosticProjectionDependencies,
	SketchEntityReferenceWarningSummary,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceDiagnosticProjection';
import type {SketchEntityReferenceRecord} from '../model/SketchEntityReferenceRecord';
import {DanglingSketchEntityReferenceConstraint} from '../relation/constraints/DanglingSketchEntityReferenceConstraint';
import {FailedSketchEntityReferenceConstraint} from '../relation/constraints/FailedSketchEntityReferenceConstraint';

/**
 * 将转换实体引用仓库中的悬空或计算失败状态投影到草图、目标实体和 On Edge 关系。
 *
 * 仓库记录是唯一事实来源；本服务持有的摘要、虚线材质状态和诊断关系均为可丢弃派生状态，
 * 因而不会形成第二套需要 Undo/Redo 的主状态。
 */
export class SketchEntityReferenceDiagnosticProjectionService {
	/** 各草图最近一次投影出的警告摘要。 */
	private readonly warningSummaryBySketchId = new Map<string, SketchEntityReferenceWarningSummary>();
	/** 已投影引用的目标位置，用于引用删除后的定向清理。 */
	private readonly projectedTargetByReferenceId = new Map<string, {
		sketchId: string;
		entityId: string;
	}>();

	/**
	 * @param dependencies 仓库和草图运行时依赖。
	 */
	public constructor(
		private readonly dependencies: SketchEntityReferenceDiagnosticProjectionDependencies,
	) {}

	/**
	 * 刷新指定目标草图的全部转换实体引用诊断。
	 *
	 * @param targetSketchIds 需要重新投影的草图持久 ID。
	 */
	public refreshSketches(targetSketchIds: readonly string[]): void {
		Array.from(new Set(targetSketchIds)).forEach((targetSketchId) => {
			this.refreshSketch(targetSketchId);
		});
	}

	/**
	 * 读取目标草图当前警告摘要。
	 *
	 * @param targetSketchId 目标草图持久 ID。
	 * @returns 不可变语义的摘要副本。
	 */
	public getSketchWarningSummary(
		targetSketchId: string,
	): SketchEntityReferenceWarningSummary {
		const summary = this.warningSummaryBySketchId.get(targetSketchId);
		if (!summary) {
			return this.createEmptySummary();
		}
		return structuredClone(summary);
	}

	/**
	 * 清除全部派生实体状态和瞬态诊断关系。
	 */
	public dispose(): void {
		this.projectedTargetByReferenceId.forEach((target, referenceId) => {
			this.clearProjectedReference(referenceId, target.sketchId, target.entityId);
		});
		this.projectedTargetByReferenceId.clear();
		this.warningSummaryBySketchId.clear();
	}

	/**
	 * 刷新单个草图。
	 *
	 * @param targetSketchId 目标草图持久 ID。
	 */
	private refreshSketch(targetSketchId: string): void {
		const references = this.dependencies.repository.captureReferencesByTargetSketchIds([
			targetSketchId,
		]);
		const currentReferenceIds = new Set(references.map((reference) => reference.referenceId));
		const touchedEntityIds = new Set(references.map((reference) => reference.targetEntityId));

		this.projectedTargetByReferenceId.forEach((target, referenceId) => {
			if (target.sketchId !== targetSketchId || currentReferenceIds.has(referenceId)) {
				return;
			}
			touchedEntityIds.add(target.entityId);
			this.clearProjectedReference(referenceId, target.sketchId, target.entityId);
			this.projectedTargetByReferenceId.delete(referenceId);
		});

		const danglingReferences = references.filter((reference) => reference.status === 'dangling');
		const danglingEntityIds = new Set(danglingReferences.map((reference) => reference.targetEntityId));
		const failedReferences = references.filter((reference) => reference.status === 'failed');
		const failedEntityIds = new Set(failedReferences.map((reference) => reference.targetEntityId));

		references.forEach((reference) => {
			const previousTarget = this.projectedTargetByReferenceId.get(reference.referenceId);
			if (
				previousTarget
				&& (
					previousTarget.sketchId !== reference.targetSketchId
					|| previousTarget.entityId !== reference.targetEntityId
				)
			) {
				// 替换式重建可能保留 referenceId 并更换目标实体，旧目标上的派生警告必须同步清除。
				this.clearProjectedReference(
					reference.referenceId,
					previousTarget.sketchId,
					previousTarget.entityId,
				);
			}
			this.projectedTargetByReferenceId.set(reference.referenceId, {
				sketchId: reference.targetSketchId,
				entityId: reference.targetEntityId,
			});
			this.projectReference(reference);
		});

		touchedEntityIds.forEach((entityId) => {
			const target = this.resolveTargetEdge(targetSketchId, entityId);
			if (failedEntityIds.has(entityId)) {
				target?.setReferenceDiagnosticState('failed');
				return;
			}
			if (danglingEntityIds.has(entityId)) {
				target?.setReferenceDiagnosticState('dangling');
				return;
			}
			target?.setReferenceDiagnosticState('normal');
		});

		this.warningSummaryBySketchId.set(
			targetSketchId,
			this.createWarningSummary(danglingReferences, failedReferences),
		);
	}

	/**
	 * 投影单条引用。
	 *
	 * @param reference 当前引用记录。
	 */
	private projectReference(reference: SketchEntityReferenceRecord): void {
		const target = this.resolveTargetEdge(reference.targetSketchId, reference.targetEntityId);
		if (!target) {
			return;
		}

		const constraints = target.constraints.getRelations();
		const referenceConstraints = constraints.filter((constraint) => (
			Reflect.get(constraint, 'referenceId') === reference.referenceId
		));

		if (reference.status === 'active') {
			referenceConstraints.forEach((constraint) => {
				if (constraint.isTransientDiagnosticConstraint) {
					target.constraints.removeRelation(constraint.id);
					return;
				}
				constraint.clearDangling();
				constraint.clearFailed();
			});
			return;
		}

		if (reference.status === 'failed') {
			const diagnosticMessage = reference.diagnostic?.message
				?? '转换实体引用计算失败，已保留上一次有效投影。';
			referenceConstraints.forEach((constraint) => {
				target.constraints.removeRelation(constraint.id);
			});
			target.constraints.addRelation(
				new FailedSketchEntityReferenceConstraint(
					reference.referenceId,
					diagnosticMessage,
					reference.sourceDisplayLabel,
				),
			);
			return;
		}

		if (reference.status !== 'dangling') {
			return;
		}

		const diagnosticMessage = reference.diagnostic?.message
			?? '转换实体引用的来源已丢失，关系处于悬空状态。';
		target.setReferenceDiagnosticState('dangling');
		const activeReferenceConstraints = referenceConstraints.filter((constraint) => {
			if (!constraint.isTransientDiagnosticConstraint) {
				return true;
			}
			target.constraints.removeRelation(constraint.id);
			return false;
		});
		if (activeReferenceConstraints.length > 0) {
			activeReferenceConstraints.forEach((constraint) => constraint.markDangling(diagnosticMessage));
			return;
		}

		target.constraints.addRelation(
			new DanglingSketchEntityReferenceConstraint(
				reference.referenceId,
				diagnosticMessage,
				reference.sourceDisplayLabel,
			),
		);
	}

	/**
	 * 清理一条已不存在引用留下的派生状态。
	 *
	 * @param referenceId 引用记录 ID。
	 * @param targetSketchId 目标草图 ID。
	 * @param targetEntityId 目标实体 ID。
	 */
	private clearProjectedReference(
		referenceId: string,
		targetSketchId: string,
		targetEntityId: string,
	): void {
		const target = this.resolveTargetEdge(targetSketchId, targetEntityId);
		if (!target) {
			return;
		}
		target.constraints.getRelations().forEach((constraint) => {
			if (Reflect.get(constraint, 'referenceId') !== referenceId) {
				return;
			}
			if (constraint.isTransientDiagnosticConstraint) {
				target.constraints.removeRelation(constraint.id);
				return;
			}
			constraint.clearDangling();
			constraint.clearFailed();
		});
		target.setReferenceDiagnosticState('normal');
	}

	/**
	 * 解析可承载转换实体引用诊断的草图边。
	 *
	 * @param targetSketchId 目标草图 ID。
	 * @param targetEntityId 目标实体 ID。
	 * @returns 草图边；对象缺失或类型不符时返回 null。
	 */
	private resolveTargetEdge(
		targetSketchId: string,
		targetEntityId: string,
	): AbstractSketchEdgeObject | null {
		const target = this.dependencies.runtime.getSketchObject(targetSketchId, targetEntityId);
		return isAbstractSketchEdgeObject(target) ? target : null;
	}

	/**
	 * @param danglingReferences 当前草图的悬空引用。
	 * @returns 草图级警告摘要。
	 */
	private createWarningSummary(
		danglingReferences: readonly SketchEntityReferenceRecord[],
		failedReferences: readonly SketchEntityReferenceRecord[],
	): SketchEntityReferenceWarningSummary {
		const diagnosticReferences = [...failedReferences, ...danglingReferences];
		return {
			status: failedReferences.length > 0
				? 'error'
				: danglingReferences.length > 0
					? 'warning'
					: 'ok',
			danglingReferenceCount: danglingReferences.length,
			failedReferenceCount: failedReferences.length,
			referenceIds: diagnosticReferences.map((reference) => reference.referenceId),
			targetEntityIds: Array.from(new Set(
				diagnosticReferences.map((reference) => reference.targetEntityId),
			)),
			diagnostics: diagnosticReferences.flatMap((reference) => (
				reference.diagnostic ? [structuredClone(reference.diagnostic)] : []
			)),
		};
	}

	/** @returns 无警告草图摘要。 */
	private createEmptySummary(): SketchEntityReferenceWarningSummary {
		return {
			status: 'ok',
			danglingReferenceCount: 0,
			failedReferenceCount: 0,
			referenceIds: [],
			targetEntityIds: [],
			diagnostics: [],
		};
	}
}
